import { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  Heading,
  Skeleton,
  Flex,
  Box,
  Text,
  Button,
  Icon,
  Input,
  SimpleGrid,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { CoupomAPI } from '../../api';
import { VtexCoupom } from '../../models/vtex-coupom';
import { CoupomFormContext } from '../../context/CreateCoupomFormContext';
import { truncate } from '../../../../utils/string.util';

type CoupomVtexProps = {
  data: VtexCoupom;
};

const CoupomVtex: FC<CoupomVtexProps> = ({ data }) => {
  const toast = useToast();
  const setFormData = useContext(CoupomFormContext)[1];
  const [isLoading, setIsLoading] = useState(false);
  const handleAdd = async () => {
    if (data.exists) return;
    setIsLoading(true);
    const coupomAPI = new CoupomAPI();
    const promise = coupomAPI
      .findVtexCouponById(data.idCalculatorConfiguration)
      .catch(err => {
        toast({
          title: 'Erro ao buscar cupom na VTEX',
          description: `Ocorreu um erro ao buscar o cupom: ${err.response.data.message}`,
          status: 'error',
          position: 'top-right',
        });
        throw err;
      })
      .finally(() => setIsLoading(false));

    const vtexCoupom = await promise;
    setFormData(state => ({
      ...state,
      vtexCoupom,
      hasVtexCoupom: true,
    }));
  };
  return (
    <>
      <Flex
        gridGap="8"
        shadow="md"
        textAlign="left"
        alignItems="center"
        h="fit-content"
        w="236px"
        transition="all 0.2s"
        _hover={
          data.exists ? undefined : { transform: 'scale(1.01)', shadow: 'lg' }
        }
        filter={data.exists ? 'grayscale(100%)' : 'none'}
        opacity={data.exists ? '0.5' : '1'}
        disabled={data.exists}
        position="relative"
        cursor="default"
        onClick={handleAdd}
      >
        <Box px={6} py={2} cursor="pointer">
          {data.exists && (
            <Text
              as="em"
              size="sm"
              fontSize="xs"
              opacity="0.8"
              fontStyle="italic"
            >
              Cupom já cadastrado
            </Text>
          )}
          <Text size="sm" fontSize="xs" opacity="0.8" wordBreak="break-all">
            Cód.: {truncate(data.utmSource, 20)}
          </Text>
          <Heading as="h2" size="sm" fontWeight="bold" color="cyan.600">
            {data.name}
          </Heading>
          {/* <Text size="sm" fontSize="xs" title={data.description} mt="1">
            {truncate(data.description, 100)}
          </Text> */}
          <Text
            size="sm"
            fontSize="xs"
            opacity="0.8"
            title={data.description}
            mt="1"
          >
            início: {data.beginDate.toLocaleDateString()} | fim:{' '}
            {data.endDate.toLocaleDateString()}
          </Text>
        </Box>

        <Button
          position="absolute"
          right="0"
          top="1"
          bg="transparent"
          _hover={{ bg: 'gray.100' }}
          disabled={data.exists}
        >
          {isLoading ? (
            <Spinner
              thickness="2px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="sm"
            />
          ) : (
            <Icon as={AiOutlinePlusCircle} w={5} h={5} color="cyan.600" />
          )}
        </Button>
      </Flex>
    </>
  );
};

const LoadingSkeleton: FC = () => {
  return (
    <>
      <Flex gridGap="3">
        <Skeleton w="150px" h="50px" alignSelf="center" />
        <Skeleton w="150px" h="50px" alignSelf="center" />
        <Skeleton w="150px" h="50px" alignSelf="center" />
      </Flex>
    </>
  );
};

const CoupomVtexList: FC = () => {
  const vtexCoupons = useRef<VtexCoupom[]>([]);
  const [filtered, setFiltered] = useState<VtexCoupom[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const filter = (value?: string) => {
    if (!value) {
      setFiltered(vtexCoupons.current);
      return;
    }
    setFiltered(
      vtexCoupons.current.filter(
        item =>
          item.name.toLowerCase().includes(value.toLowerCase()) ||
          item.utmSource.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  };

  useEffect(() => {
    setLoading(true);
    const coupomAPI = new CoupomAPI();
    coupomAPI
      .findVtexCoupons()
      .then(res => {
        vtexCoupons.current = res;
        filter();
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Flex direction="column" maxH="60svh" position="relative">
      <Heading as="h2" size="sm">
        Cupons ativos na VTEX
      </Heading>
      {loading ? (
        <Flex mt="5">
          <LoadingSkeleton />
        </Flex>
      ) : (
        <>
          <Flex alignItems="center" gridGap="2" mt="2" mb="4">
            <Text as="label" size="sm">
              Buscar:
            </Text>
            <Input
              size="sm"
              rounded="base"
              placeholder="digite o código ou nome do cupom"
              maxW="256px"
              onChange={e => filter(e.target.value)}
            />
          </Flex>
          <Flex overflowX="auto">
            <SimpleGrid as="section" columns={2} gridGap="3">
              {filtered.length ? (
                filtered.map(item => (
                  <CoupomVtex
                    key={item.idCalculatorConfiguration}
                    data={item}
                  />
                ))
              ) : (
                <Text textAlign="center" w="full" opacity="0.7" mt="5">
                  Ainda não há cupons cadastrados. Adicione um cupom na VTEX.
                </Text>
              )}
            </SimpleGrid>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default CoupomVtexList;
